import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  useTheme,
} from '@mui/material';
import CustomIconButton from './CustomIconButton';
import CustomDateTimePicker from './CustomDateTimePicker';
import InputField from './InputField';
import dayjs from 'dayjs';

interface RequestedVideoModalProps {
  open: boolean;
  onClose: () => void;
  handleRequested: (data: any) => void;
  title?: string;
  description?: string;
  cameraArr?: any[];
}

const formatTime = (time: string | null): string => {
  if (!time) return '';

  let lowerTime = '';
  if (time.trim()) {
    lowerTime = time.trim().toLowerCase();
  }

  const timeMatch = lowerTime.match(/^(\d{1,2}):?(\d{2})?\s*(am|pm)?$/);
  if (!timeMatch) return '';

  let [_, hoursStr, minutesStr, meridian] = timeMatch;
  let hours = parseInt(hoursStr, 10);
  const minutes = minutesStr ? parseInt(minutesStr, 10) : 0;

  if (isNaN(hours) || hours < 0 || hours > 12) return '';

  if (meridian) {
    if (meridian === 'pm' && hours !== 12) {
      hours += 12;
    } else if (meridian === 'am' && hours === 12) {
      hours = 0;
    }
  }

  const formattedHours = hours.toString().padStart(2, '0');
  const formattedMinutes = minutes.toString().padStart(2, '0');
  return `${formattedHours}:${formattedMinutes}:00`;
};

const formatDate = (date: string | null) => {
  if (!date) return '';
  const parsedDate = dayjs(date, 'DD/MM/YYYY');
  if (!parsedDate.isValid()) return '';
  return parsedDate.format('YYYY-MM-DD');
};

const now = dayjs();
const end = now;
const start = end.subtract(1, 'hour');

const initialFormData = {
  startDate: start.format('DD/MM/YYYY'),
  endDate: end.format('DD/MM/YYYY'),
  startTime: start.format('hh:mm A'),
  endTime: end.format('hh:mm A'),
  selectedOption: '',
};

const RequestedVideoModal: React.FC<RequestedVideoModalProps> = ({
  open,
  onClose,
  handleRequested,
  title = 'Request Video',
  description = 'Choose the date, time and camera for video playback.',
  cameraArr = [],
}) => {
  const theme = useTheme();
  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState<any>({});
  const [emailInputs, setEmailInputs] = useState<string[]>(['']);
  const [error, setError] = useState('');

  const handleFieldChange = (field: string, value: string | null) => {
    setFormData((prevState) => ({
      ...prevState,
      [field]: value,
    }));

    setFormErrors((prevErrors: any) => {
      const newErrors = { ...prevErrors };
      if (value) {
        delete newErrors[field];
      }
      return newErrors;
    });
  };

  const handleDateTimeChange = (type: 'start' | 'end', val: string | null) => {
    if (val) {
      const [date, ...timeParts] = val.split(' ');
      const time = timeParts.join(' ');
      handleFieldChange(`${type}Date`, date);
      handleFieldChange(`${type}Time`, time);

      setFormData((prev) => {
        const startDT = dayjs(
          `${type === 'start' ? date : prev.startDate} ${type === 'start' ? time : prev.startTime}`,
          'DD/MM/YYYY hh:mm A'
        );
        const endDT = dayjs(
          `${type === 'end' ? date : prev.endDate} ${type === 'end' ? time : prev.endTime}`,
          'DD/MM/YYYY hh:mm A'
        );

        if (type === 'start' && startDT.isAfter(endDT)) {
          const newEnd = startDT.add(1, 'minute');
          return {
            ...prev,
            endDate: newEnd.format('DD/MM/YYYY'),
            endTime: newEnd.format('hh:mm A'),
          };
        }
        if (type === 'end' && endDT.isBefore(startDT)) {
          const newStart = endDT.subtract(1, 'minute');
          return {
            ...prev,
            startDate: newStart.format('DD/MM/YYYY'),
            startTime: newStart.format('hh:mm A'),
          };
        }
        return prev;
      });
    } else {
      handleFieldChange(`${type}Date`, null);
      handleFieldChange(`${type}Time`, null);
    }
  };

  const addEmailInput = () => {
    if (emailInputs.length < 5) {
      setEmailInputs([...emailInputs, '']);
    }
  };

  const removeEmailInput = (index: number) => {
    if (emailInputs.length > 1) {
      const updatedEmails = emailInputs.filter((_, i) => i !== index);
      setEmailInputs(updatedEmails);
    }
  };

  const handleEmailChange = (index: number, value: string) => {
    const updatedEmails = [...emailInputs];
    updatedEmails[index] = value;
    setEmailInputs(updatedEmails);
  };

  const handleCheckboxChange = (value: string) => {
    setFormData((prevState) => ({
      ...prevState,
      selectedOption: prevState.selectedOption === value ? '' : value,
    }));
    setFormErrors((prevErrors: any) => ({
      ...prevErrors,
      selectedOption: '',
    }));
  };

  const validateForm = (): boolean => {
    let errors: any = {};
    let isValid = true;

    if (!formData.startDate) {
      errors.startDate = 'Start date is required';
      isValid = false;
    }
    if (!formData.endDate) {
      errors.endDate = 'End date is required';
      isValid = false;
    }
    if (!formData.startTime) {
      errors.startTime = 'Start time is required';
      isValid = false;
    }
    if (!formData.endTime) {
      errors.endTime = 'End time is required';
      isValid = false;
    }
    if (!formData.selectedOption) {
      errors.selectedOption = 'Camera is required';
      isValid = false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const domainRegex = /^[a-zA-Z0-9-]+\.(com)$/;
    const validEmails = emailInputs.filter(email => email.trim() !== '');

    if (validEmails.length > 0) {
      const emailErrors: string[] = [];

      validEmails.forEach((email, index) => {
        if (!emailRegex.test(email)) {
          emailErrors.push(`Invalid email at position ${index + 1}`);
        } else {
          const domainPart = email.split('@')[1];
          if (
            domainPart &&
            (!domainRegex.test(domainPart) || domainPart.split('.').length !== 2)
          ) {
            emailErrors.push(`Invalid domain at position ${index + 1}`);
          }
        }
      });

      if (emailErrors.length > 0) {
        errors.emailIds = emailErrors;
        isValid = false;
      }
    }

    if (formData.startDate && formData.endDate) {
      const startDate = dayjs(formData.startDate, 'DD/MM/YYYY');
      const endDate = dayjs(formData.endDate, 'DD/MM/YYYY');

      if (startDate.isAfter(endDate)) {
        errors.startDate = 'End date cannot be before start date';
        isValid = false;
      }

      if (formData.startTime && formData.endTime) {
        const startDateTime = dayjs(`${formData.startDate} ${formData.startTime}`, 'DD/MM/YYYY hh:mm A');
        const endDateTime = dayjs(`${formData.endDate} ${formData.endTime}`, 'DD/MM/YYYY hh:mm A');
        const now = dayjs().second(0).millisecond(0);

        if (startDate.isSame(endDate, 'day')) {
          if (startDateTime.isSame(endDateTime)) {
            errors.endTime = 'End time cannot be same as start time';
            isValid = false;
          }
          if (startDateTime.isAfter(endDateTime)) {
            errors.endTime = 'End time cannot be before start time';
            isValid = false;
          }
        }

        if (endDateTime.isAfter(now)) {
          errors.endTime = 'End time cannot be in the future';
          isValid = false;
        }
      }
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSave = () => {
    if (validateForm()) {
      const validEmails = emailInputs.filter(email => email.trim() !== '');
      const finalEmailValidation = validEmails.every(email => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const domainRegex = /^[a-zA-Z0-9-]+\.(com)$/;

        if (!emailRegex.test(email)) return false;

        const domainPart = email.split('@')[1];
        return !(domainPart && (!domainRegex.test(domainPart) || domainPart.split('.').length !== 2));
      });

      if (!finalEmailValidation) {
        setError('Please fix email validation errors before submitting.');
        return;
      }

      const formattedStartDate = formatDate(formData.startDate);
      const formattedEndDate = formatDate(formData.endDate);
      const formattedStartTime = formatTime(formData.startTime);
      const formattedEndTime = formatTime(formData.endTime);

      setError('');
      handleRequested({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        startTime: formattedStartTime,
        endTime: formattedEndTime,
        cameraType: formData.selectedOption,
        emailIds: validEmails,
      });
      onClose();
    }
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setFormErrors({});
    setEmailInputs(['']);
    setError('');
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setFormErrors({});
    setEmailInputs(['']);
    setError('');
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="requested-video-title"
      aria-describedby="requested-video-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '70%',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          overflowY: 'auto',
          maxHeight: '90vh',
        }}
      >
        <div className="flex justify-between h-full flex-col">
          <div className="flex gap-8 flex-col">
            <div className="flex flex-col gap-1">
              <div className="flex justify-between items-center">
                <Typography variant="h6">
                  {title}
                </Typography>
                <CustomIconButton
                  text="Reset All"
                  background="transparent"
                  variant="outlined"
                  onClick={handleReset}
                />
              </div>
              {description && (
                <Typography variant="button" fontWeight={400} color="secondary">
                  {description}
                </Typography>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex gap-4 sm:flex-row flex-col">
                <CustomDateTimePicker
                  label="Start Date & Time"
                  value={
                    formData.startDate && formData.startTime
                      ? `${formData.startDate} ${formData.startTime}`
                      : null
                  }
                  onChange={(val) => handleDateTimeChange('start', val)}
                  placement="top"
                  maxPopperHeight="330px"
                  minutesStep={5}
                  maxDateTime={dayjs().format('DD/MM/YYYY hh:mm A')}
                  is12HourFormat
                />
                <CustomDateTimePicker
                  label="End Date & Time"
                  value={
                    formData.endDate && formData.endTime ? `${formData.endDate} ${formData.endTime}` : null
                  }
                  onChange={(val) => handleDateTimeChange('end', val)}
                  placement="top"
                  maxPopperHeight="330px"
                  minutesStep={5}
                  maxDateTime={dayjs().format('DD/MM/YYYY hh:mm A')}
                  is12HourFormat
                />
              </div>
              {(formErrors.startDate ||
                formErrors.endDate ||
                formErrors.startTime ||
                formErrors.endTime) && (
                  <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {formErrors.endTime || formErrors.startTime || formErrors.endDate || formErrors.startDate}
                  </Typography>
                )}

              {cameraArr && cameraArr.length > 0 && (
                <>
                  <Typography>Select Camera</Typography>
                  <FormControl component="fieldset" error={!!formErrors.selectedOption}>
                    <FormGroup row>
                      {cameraArr.map((camera) => {
                        const cameraValue = typeof camera === 'string' ? camera : camera.value;
                        const cameraLabel = typeof camera === 'string' ? camera.replace('_', ' ') : camera.label;

                        return (
                          <FormControlLabel
                            key={cameraValue}
                            control={
                              <Checkbox
                                sx={{
                                  color: theme.palette.primary.main,
                                  '&.Mui-checked': {
                                    color: theme.palette.primary.main,
                                  },
                                }}
                                checked={formData.selectedOption === cameraValue}
                                onChange={() => handleCheckboxChange(cameraValue)}
                              />
                            }
                            label={<Typography sx={{ color: theme.palette.primary.main }}>{cameraLabel}</Typography>}
                          />
                        );
                      })}
                    </FormGroup>
                    {formErrors.selectedOption && (
                      <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                        {formErrors.selectedOption}
                      </Typography>
                    )}
                  </FormControl>
                </>
              )}

              {formData.selectedOption && (
                <div className="flex flex-col gap-2 mt-4">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Email Addresses
                    </Typography>
                    {emailInputs.length < 5 && (
                      <CustomIconButton
                        icon={<span style={{ fontSize: '18px', fontWeight: 'bold' }}>+</span>}
                        background="transparent"
                        onClick={addEmailInput}
                        variant="outlined"
                        text=''
                      />
                    )}
                  </Box>

                  <Box className="grid grid-cols-2 gap-2 w-full items-center">
                    {emailInputs.map((email, index) => (
                      <Box key={index} sx={{ display: 'flex', position: 'relative', alignItems: 'center', gap: 1, mb: 1 }}>
                        <InputField
                          label={`Email Address ${index + 1}`}
                          type="email"
                          placeholder="Enter email address"
                          value={email}
                          onChange={(e) => handleEmailChange(index, e.target.value)}
                        />
                        {emailInputs.length > 1 && (
                          <Box className="absolute right-0 top-1/2" sx={{ transform: 'translateY(-18%)' }}>
                            <CustomIconButton
                              icon={<span style={{ fontSize: '18px', fontWeight: 'bold' }}>-</span>}
                              background="transparent"
                              onClick={() => removeEmailInput(index)}
                              variant="outlined"
                              text=''
                            />
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>

                  {formErrors.emailIds && formErrors.emailIds.length > 0 && (
                    <Box>
                      {formErrors.emailIds.map((error: string, index: number) => (
                        <Typography key={index} color="error" variant="body2">
                          {error}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  <Typography variant="caption" color="textSecondary">
                    {emailInputs.length}/5 email addresses
                  </Typography>
                </div>
              )}

              {error && (
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              )}
            </div>
          </div>

          <div className="flex gap-2 mt-4 items-center justify-between">
            <CustomIconButton
              text="Cancel"
              background="transparent"
              outline
              padding="0px"
              variant="outlined"
              onClick={handleClose}
            />
            <CustomIconButton
              text="Request"
              outline
              padding="0px"
              variant="contained"
              onClick={handleSave}
            />
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default RequestedVideoModal;